import React from "react";
import { Check, X } from "lucide-react";
import { cvar } from "../../utils";
import { CHECK } from "../../checks";
import type { EvalResult, PerCheckScore } from "../../types";

export interface EvalPanelProps {
  ev: EvalResult;
  evalName: string;
}

/* The "Eval" tab (shown only when an eval set is attached). Displays the
   catalog-accuracy / detection-recall / memo-match score cards, plus per-email
   ground truth comparison. */
export function EvalPanel({ ev, evalName }: EvalPanelProps): JSX.Element {
  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 6,
        }}
      >
        <span className="up">Scored vs ground truth</span>
        <span className="mono tert" style={{ fontSize: 11 }}>
          {evalName}
        </span>
      </div>
      <div className="tert" style={{ fontSize: 11.5, marginBottom: 12, lineHeight: 1.5 }}>
        Compares the run's actual output to the eval set's answer key: did each email catalog to
        the expected matter, and did the expected issue (conflict, privilege, etc.) get detected.
      </div>
      {ev.catN === 0 && <div className="empty">Run the feed to score against the eval set.</div>}
      {ev.catN > 0 && (
        <>
          <div className="scorecards">
            <div className="score">
              <div className="sv" style={{ color: cvar(ev.catPct >= 80 ? "--green" : "--amber") }}>
                {ev.catPct}%
              </div>
              <div className="sl">
                Catalog Accuracy · {ev.catOK}/{ev.catN}
              </div>
              <div className="tert" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>
                Percentage of emails routed to the correct matter
              </div>
            </div>
            <div className="score">
              <div className="sv" style={{ color: cvar(ev.detPct >= 80 ? "--green" : "--amber") }}>
                {ev.detPct}%
              </div>
              <div className="sl">
                Detection Recall · {ev.detOK}/{ev.detN}
              </div>
              <div className="tert" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>
                Percentage of expected issues detected by the checks
              </div>
            </div>
            {ev.memoMatch && (
              <div className="score">
                <div
                  className="sv"
                  style={{ color: cvar(ev.memoMatch.pct >= 80 ? "--green" : "--amber") }}
                >
                  {ev.memoMatch.pct}%
                </div>
                <div className="sl">
                  Deal Memo Match · {ev.memoMatch.ok}/{ev.memoMatch.n}
                </div>
                <div className="tert" style={{ fontSize: 11, marginTop: 4, lineHeight: 1.4 }}>
                  Deal-memo fields matching the eval set's expected final state
                </div>
              </div>
            )}
          </div>
          {ev.memoMatch && ev.memoMatch.ok < ev.memoMatch.n && (
            <div className="tert" style={{ fontSize: 11, margin: "0 0 12px", lineHeight: 1.5 }}>
              Memo fields not yet matching final state:{" "}
              {ev.memoMatch.rows
                .filter((r) => !r.ok)
                .map((r) => r.key)
                .join(", ") || "none"}
            </div>
          )}
          {ev.perCheck && Object.keys(ev.perCheck).length > 0 && (
            <>
              <div className="up" style={{ margin: "12px 0 6px" }}>
                Per-check contract scores
              </div>
              {(
                [
                  ["Spine Agents", (s: PerCheckScore) => (CHECK[s.checkId]?.kind || "spine") !== "workstream"],
                  ["Workstream Agents", (s: PerCheckScore) => CHECK[s.checkId]?.kind === "workstream"],
                ] as Array<[string, (s: PerCheckScore) => boolean]>
              ).map(([hdr, filter]) => {
                const rows = Object.values(ev.perCheck!).filter(filter);
                if (!rows.length) return null;
                return (
                  <div key={hdr} style={{ marginBottom: 8 }}>
                    <div className="tert" style={{ fontSize: 11, marginBottom: 4 }}>
                      {hdr}
                    </div>
                    {rows.map((s) => {
                      const missed = s.details.filter((d) => !d.ok);
                      return (
                        <div className="erow" key={s.checkId}>
                          <span
                            className="mono"
                            style={{
                              minWidth: 38,
                              color: cvar(s.pct >= 80 ? "--green" : "--amber"),
                            }}
                          >
                            {s.pct}%
                          </span>
                          <div className="es">
                            {CHECK[s.checkId]?.name || s.checkId} · {s.correct}/{s.scored}
                          </div>
                          {missed.length > 0 && (
                            <span
                              className="ed ttrigger tert"
                              data-tip={missed
                                .slice(0, 4)
                                .map((d) => `#${d.emailId} expected ${d.expected} — got ${d.got}`)
                                .join("; ")}
                            >
                              {missed.length} missed
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </>
          )}
          {ev.trackerMatch && (
            <>
              <div className="up" style={{ margin: "12px 0 6px" }}>
                Final tracker state · F1 {ev.trackerMatch.f1.toFixed(2)} (P{" "}
                {ev.trackerMatch.precision.toFixed(2)} / R {ev.trackerMatch.recall.toFixed(2)})
              </div>
              {ev.trackerMatch.rows.map((r, i) => (
                <div className="erow" key={i}>
                  <span
                    className="mono"
                    style={{
                      minWidth: 38,
                      color: cvar(r.f1 >= 0.8 ? "--green" : "--amber"),
                    }}
                  >
                    {Math.round(r.f1 * 100)}%
                  </span>
                  <div className="es">
                    {r.label} · {r.ws} · {r.matchedN}/{r.expectedN} matched
                    {r.extraN > 0 ? `, ${r.extraN} extra` : ""}
                    {r.statusN > 0 ? ` · status ${r.statusOK}/${r.statusN}` : ""}
                  </div>
                </div>
              ))}
            </>
          )}
          {ev.alignment && (
            <>
              <div className="up" style={{ margin: "12px 0 6px" }}>
                Pipeline alignment
              </div>
              <div className="tert" style={{ fontSize: 11, marginBottom: 6, lineHeight: 1.5 }}>
                Drift = per-email outputs score well but the accumulated final state does not —
                the failure is upstream (routing, merge, carried state), not the extractor.
              </div>
              {ev.alignment.map((a) => (
                <div className="erow" key={a.checkId}>
                  <span
                    className="mono"
                    style={{
                      minWidth: 70,
                      color: cvar(a.verdict === "aligned" ? "--green" : "--amber"),
                    }}
                  >
                    {a.verdict}
                  </span>
                  <div className="es">
                    {a.label} · per-email {a.perEmailPct ?? "—"}% → final {a.finalPct ?? "—"}%
                  </div>
                </div>
              ))}
            </>
          )}
          <div className="up" style={{ margin: "12px 0 6px" }}>
            Per-email
          </div>
          {ev.rows.map((r) => (
            <div className="erow" key={r.id}>
              {r.catalogOK ? (
                <Check size={15} color={cvar("--green")} />
              ) : (
                <X size={15} color={cvar("--red")} />
              )}
              <div className="es">
                #{r.id} {r.subject}
              </div>
              {r.dims.map((d, i) => (
                <span
                  className="ed ttrigger"
                  key={i}
                  style={{ color: cvar(d.detectOK ? "--green" : "--red") }}
                  data-tip={
                    "Expected " +
                    d.dim +
                    " issue " +
                    (d.detectOK ? "was detected by the checks." : "was missed by the checks.")
                  }
                >
                  {d.dim} {d.detectOK ? "✓" : "✗"}
                </span>
              ))}
            </div>
          ))}
        </>
      )}
    </div>
  );
}
